import React, { useEffect, useState } from 'react';
import classnames from 'classnames';
import { Modal } from '@zurich-base-ui/components.modal';
import { formatBytes } from 'app/utils';
import {
  getGeneratingExtraction,
  useAppSelector,
  setGeneratingExtraction,
  useAppDispatch,
  getCurrentFile,
  getService
} from 'app/redux';
import GeneratingExtractionPictogram from 'app/assets/UploadingSpinner.svg';
import styles from './GeneratingExtraction.module.scss';
import { SERVICE_NAMES } from 'app/utils/consts';
import { CircularProgressBar } from 'app/components/CircularProgressBar';
import { IGeneratingExtraction } from './GeneratingExtraction.interface';

const GeneratingExtraction: React.FC<IGeneratingExtraction> = ({
  processTime,
  file,
  caption = 'Please, be aware that complex files containing numerous images or tables may challenge our machine learning.'
}) => {
  const dispatch = useAppDispatch();
  const isGeneratingExtraction: boolean = useAppSelector(
    getGeneratingExtraction
  );
  const currentFile = useAppSelector(getCurrentFile) || file;
  const currentService = useAppSelector(getService);
  const [progress, setProgress] = useState(0);
  const maxProgress = 99.9;
  const noFilenameDisplay = [
    SERVICE_NAMES.CLAIMS_EMAIL_SUMMARY,
    SERVICE_NAMES.TRANSLATOR,
    SERVICE_NAMES.COVERAGE_CHECK_V2
  ];

  useEffect(() => {
    setProgress(0);
    if (processTime && isGeneratingExtraction) {
      const interval = setInterval(() => {
        setProgress((prevProgress) => {
          const newProgress = prevProgress + maxProgress / (processTime / 1000);
          if (newProgress >= maxProgress) {
            clearInterval(interval);
            return maxProgress;
          }
          return newProgress;
        });
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [processTime, isGeneratingExtraction]);

  return (
    <Modal
      on={isGeneratingExtraction}
      onClose={() => dispatch(setGeneratingExtraction(false))}
      variant="slim"
    >
      <div className={styles.modal}>
        <div>
          <h2 className="h1">Processing</h2>
          {(!currentService || !noFilenameDisplay.includes(currentService)) && (
            <small className="caption-sm-r">
              <span
                className={classnames({
                  [styles.filename]:
                    currentFile != null && currentFile?.name.length > 25
                })}
              >
                {currentFile != null && currentFile?.name.length > 25
                  ? currentFile?.name.slice(0, 25) + '...'
                  : currentFile?.name}
                {currentFile != null && currentFile?.name.length > 25 && (
                  <span className={styles.tooltip}>{currentFile?.name}</span>
                )}
              </span>{' '}
              |{' '}
              <b className="caption-sm-m">
                {currentFile != null && formatBytes(currentFile.size)}
              </b>
            </small>
          )}
        </div>
        <div className={styles.main}>
          {(() => {
            if (processTime) {
              return <CircularProgressBar progress={progress} />;
            }
            return (
              <img
                src={GeneratingExtractionPictogram}
                alt="Generating extraction..."
                className={styles.pictogram}
              />
            );
          })()}
          <p className="caption-sm-r">{caption}</p>
        </div>
      </div>
    </Modal>
  );
};

export default GeneratingExtraction;
